import React, { useState, useEffect, useRef } from 'react';
import { db, functions } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import RouletteWheel from './RouletteWheel';
import { useToast } from '../ToastContext';

function RoulettePage({ user, userData, onUserDataChange, onTokenAdjustmentChange, onBack, isMobile }) {
  const toast = useToast();
  const [bets, setBets] = useState([]); // Array of {type, value, amount, tokenType}
  const [betAmount, setBetAmount] = useState(1);
  const [tokenType, setTokenType] = useState("crewmate"); // "crewmate" or "imposter"
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [timeUntilSpin, setTimeUntilSpin] = useState(30);
  const [gameState, setGameState] = useState(null);
  const [winNotification, setWinNotification] = useState(null); // {won: boolean, amount: number}
  const [recentPlayerBets, setRecentPlayerBets] = useState([]); // Recent bets from other players
  // Track whether we've already reserved tokens for the current local bets
  const reservedTokensRef = useRef({ crewmate: 0, imposter: 0 });
  // Snapshot of user balance at the start of each betting round (before any bets placed)
  const balanceAtRoundStartRef = useRef({ crewmate: 0, imposter: 0 });
  // Set to true when user manually clears bets so the spin payout skips them
  const betsClearedRef = useRef(false);
  // Prevents clearBets from running concurrently (spam clicking)
  const clearingBetsRef = useRef(false);
  // Set to true once processBets has started for the current bets — unmount cleanup must NOT refund after this
  const betsPayoutStartedRef = useRef(false);
  // Sequence counter so only the latest in-flight placeBets call can roll back on error
  const betSequenceRef = useRef(0);
  // Prevents stuck-spin recovery from firing repeatedly
  const stuckSpinRecoveryFiredRef = useRef(false);

  // American Roulette numbers with colors
  const numbers = [
    { num: 0, color: "green" },
    { num: "00", color: "green" },
    ...Array.from({ length: 36 }, (_, i) => {
      const num = i + 1;
      const redNumbers = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36];
      return { num, color: redNumbers.includes(num) ? "red" : "black" };
    })
  ];

  // processBets removed — payout, stats, and transaction logging all handled
  // server-side in the triggerSpin Cloud Function.

  function calculatePayout(bet, winningNumber) {
    const { type, value, amount } = bet;
    let isWin = false;
    let multiplier = 0;

    switch (type) {
      case "straight": // Single number
        isWin = String(value) === String(winningNumber.num);
        multiplier = 35; // 35:1
        break;
      case "red":
        isWin = winningNumber.color === "red";
        multiplier = 1; // 1:1
        break;
      case "black":
        isWin = winningNumber.color === "black";
        multiplier = 1; // 1:1
        break;
      case "green":
        isWin = winningNumber.color === "green";
        multiplier = 17; // 17:1 for green
        break;
      case "even":
        isWin = typeof winningNumber.num === "number" && winningNumber.num !== 0 && winningNumber.num % 2 === 0;
        multiplier = 1; // 1:1
        break;
      case "odd":
        isWin = typeof winningNumber.num === "number" && winningNumber.num !== 0 && winningNumber.num % 2 === 1;
        multiplier = 1; // 1:1
        break;
      case "range":
        if (value === "1-18") {
          isWin = typeof winningNumber.num === "number" && winningNumber.num >= 1 && winningNumber.num <= 18;
        } else if (value === "19-36") {
          isWin = typeof winningNumber.num === "number" && winningNumber.num >= 19 && winningNumber.num <= 36;
        }
        multiplier = 1; // 1:1
        break;
      case "1-18":
        isWin = typeof winningNumber.num === "number" && winningNumber.num >= 1 && winningNumber.num <= 18;
        multiplier = 1; // 1:1
        break;
      case "19-36":
        isWin = typeof winningNumber.num === "number" && winningNumber.num >= 19 && winningNumber.num <= 36;
        multiplier = 1; // 1:1
        break;
      case "dozen":
        if (value === 1) {
          isWin = typeof winningNumber.num === "number" && winningNumber.num >= 1 && winningNumber.num <= 12;
        } else if (value === 2) {
          isWin = typeof winningNumber.num === "number" && winningNumber.num >= 13 && winningNumber.num <= 24;
        } else if (value === 3) {
          isWin = typeof winningNumber.num === "number" && winningNumber.num >= 25 && winningNumber.num <= 36;
        }
        multiplier = 2; // 2:1
        break;
      case "1st12":
        isWin = typeof winningNumber.num === "number" && winningNumber.num >= 1 && winningNumber.num <= 12;
        multiplier = 2; // 2:1
        break;
      case "2nd12":
        isWin = typeof winningNumber.num === "number" && winningNumber.num >= 13 && winningNumber.num <= 24;
        multiplier = 2; // 2:1
        break;
      case "3rd12":
        isWin = typeof winningNumber.num === "number" && winningNumber.num >= 25 && winningNumber.num <= 36;
        multiplier = 2; // 2:1
        break;
      case "column":
        if (value === 1) {
          isWin = typeof winningNumber.num === "number" && [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36].includes(winningNumber.num);
        } else if (value === 2) {
          isWin = typeof winningNumber.num === "number" && [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35].includes(winningNumber.num);
        } else if (value === 3) {
          isWin = typeof winningNumber.num === "number" && [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34].includes(winningNumber.num);
        }
        multiplier = 2; // 2:1
        break;
      case "column1":
        isWin = typeof winningNumber.num === "number" && [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34].includes(winningNumber.num);
        multiplier = 2; // 2:1
        break;
      case "column2":
        isWin = typeof winningNumber.num === "number" && [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35].includes(winningNumber.num);
        multiplier = 2; // 2:1
        break;
      case "column3":
        isWin = typeof winningNumber.num === "number" && [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36].includes(winningNumber.num);
        multiplier = 2; // 2:1
        break;
      case "split": // 2 adjacent numbers
        isWin = Array.isArray(value) && value.includes(winningNumber.num);
        multiplier = 17; // 17:1
        break;
      case "street": // 3 numbers in a row
        isWin = Array.isArray(value) && value.includes(winningNumber.num);
        multiplier = 11; // 11:1
        break;
      case "corner": // 4 numbers in a square
        isWin = Array.isArray(value) && value.includes(winningNumber.num);
        multiplier = 8; // 8:1
        break;
      case "sixline": // 6 numbers (two rows)
        isWin = Array.isArray(value) && value.includes(winningNumber.num);
        multiplier = 5; // 5:1
        break;
      default:
        break;
    }

    return isWin ? parseFloat((amount * (multiplier + 1)).toFixed(2)) : 0; // Return bet + winnings
  }

  // Initialize or sync with global roulette game state
  useEffect(() => {
    const rouletteRef = doc(db, "casino", "roulette");

    // Listen to roulette game state
    let retryTimeout = null;
    let currentUnsub = null;

    function subscribe() {
      currentUnsub = onSnapshot(rouletteRef, async (docSnap) => {
        const now = Date.now();

        if (!docSnap.exists()) {
          // Doc doesn't exist yet — call triggerSpin which will seed it with a 30s betting window
          const triggerSpinFn = httpsCallable(functions, 'triggerSpin');
          triggerSpinFn().catch(() => {});
        } else {
          const data = docSnap.data();
          console.log("Firestore update:", { isSpinning: data.isSpinning, lastResult: data.lastResult, nextSpinTime: new Date(data.nextSpinTime).toLocaleTimeString() });

          setGameState(data);

          // Update local state from Firestore
          setIsSpinning(data.isSpinning || false);
          // Immediately sync timer to prevent flicker showing "LOCKED 0s" after spin ends
          if (data.nextSpinTime) {
            const remaining = Math.max(0, Math.ceil((data.nextSpinTime - now) / 1000));
            setTimeUntilSpin(remaining);
          }
          if (data.lastResult) {
            setResult(data.lastResult);
          }
          if (data.history) {
            setHistory(data.history);
          }
          // Sync active bets from all players (filter out null entries from cleared bets)
          if (data.activeBets) {
            setRecentPlayerBets(Object.values(data.activeBets).filter(Boolean));
          } else {
            setRecentPlayerBets([]);
          }
        }
      }, (error) => {
        // Listener died — retry after 3 seconds so the user isn't stuck forever
        console.error("Roulette listener error, retrying in 3s:", error);
        retryTimeout = setTimeout(subscribe, 3000);
      });
    }

    subscribe();

    return () => {
      if (currentUnsub) currentUnsub();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process bets when spin completes
  useEffect(() => {
    // Only trigger payout when isSpinning transitions from true → false (spin just ended)
    // Guarding on this transition prevents stale result from firing payout on mount or
    // when Firestore snapshots re-set result to the same value (new object reference).
    const spinJustEnded = prevIsSpinningRef.current === true && isSpinning === false;
    prevIsSpinningRef.current = isSpinning;

    const currentBets = betsForCleanupRef.current;
    if (spinJustEnded && result && currentBets.length > 0) {
      // Mark payout as started so unmount cleanup won't try to refund
      betsPayoutStartedRef.current = true;
      // Server already paid out via triggerSpin Cloud Function.
      // Client only needs to show a notification and clear local bet state.
      if (!betsClearedRef.current) {
        const crewmateBetTotal = parseFloat(currentBets.filter(b => b.tokenType === "crewmate").reduce((s, b) => s + b.amount, 0).toFixed(2));
        const imposterBetTotal = parseFloat(currentBets.filter(b => b.tokenType === "imposter").reduce((s, b) => s + b.amount, 0).toFixed(2));
        const crewmateWin = parseFloat(currentBets.filter(b => b.tokenType === "crewmate").reduce((sum, b) => sum + calculatePayout(b, result), 0).toFixed(2));
        const imposterWin = parseFloat(currentBets.filter(b => b.tokenType === "imposter").reduce((sum, b) => sum + calculatePayout(b, result), 0).toFixed(2));
        const netProfit = parseFloat(((crewmateWin + imposterWin) - (crewmateBetTotal + imposterBetTotal)).toFixed(2));
        const notificationLines = [];
        if (crewmateBetTotal > 0) notificationLines.push({ type: "crewmate", net: crewmateWin - crewmateBetTotal, win: crewmateWin });
        if (imposterBetTotal > 0) notificationLines.push({ type: "imposter", net: imposterWin - imposterBetTotal, win: imposterWin });
        setWinNotification({ won: netProfit > 0, amount: Math.abs(netProfit), lines: notificationLines });
        setTimeout(() => setWinNotification(null), 5000);
        // Refresh balance from server after payout
        onUserDataChange();
      }
      betsClearedRef.current = false;
      betsPayoutStartedRef.current = false;
      reservedTokensRef.current = { crewmate: 0, imposter: 0 };
      balanceAtRoundStartRef.current = { crewmate: 0, imposter: 0 };
      betSequenceRef.current = 0;
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ frozen: false, crewmate: 0, imposter: 0 });
      setBets([]);
    } else if (!isSpinning && !spinJustEnded) {
      betsClearedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpinning, result]);

  // Keep refs up to date so the unmount cleanup always sees current values without re-registering
  const prevIsSpinningRef = useRef(false);
  const betsForCleanupRef = useRef(bets);
  const isSpinningRef = useRef(isSpinning);
  useEffect(() => { betsForCleanupRef.current = bets; }, [bets]);
  useEffect(() => { isSpinningRef.current = isSpinning; }, [isSpinning]);

  // On unmount: call server clearBets so tokens are refunded if user navigates away mid-round.
  // Server enforces this atomically — no client-side token writes needed.
  useEffect(() => {
    return () => {
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ frozen: false, crewmate: 0, imposter: 0 });
      if (betsForCleanupRef.current.length > 0 && !betsClearedRef.current && !betsPayoutStartedRef.current) {
        const fn = httpsCallable(functions, 'clearBets');
        fn().catch(() => {}); // best-effort; server will handle it
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timer countdown and spin trigger
  useEffect(() => {
    if (!gameState) return;

    // Immediately sync timer when gameState changes (don't wait for first interval tick)
    const immediateNow = Date.now();
    const immediateRemaining = Math.max(0, Math.ceil((gameState.nextSpinTime - immediateNow) / 1000));
    setTimeUntilSpin(immediateRemaining);

    const interval = setInterval(async () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((gameState.nextSpinTime - now) / 1000));
      setTimeUntilSpin(remaining);

      if (gameState.lastResult) {
        setResult(gameState.lastResult);
      }

      if (gameState.history) {
        setHistory(gameState.history);
      }

      // Check if it's time to spin and not already spinning
      if (now >= gameState.nextSpinTime && !gameState.isSpinning && !isSpinningRef.current) {
        // Set spinning immediately via ref to prevent this interval from firing again
        // before the Firestore snapshot arrives with isSpinning=true from the server
        isSpinningRef.current = true;
        // Winning number is generated server-side — clients cannot manipulate the result
        const triggerSpinFn = httpsCallable(functions, 'triggerSpin');
        triggerSpinFn().catch((error) => {
          // Delay retry by 3s to avoid burning through the 5-call/60s rate limit
          // on rapid failures (e.g. race condition, network errors).
          // The Firestore snapshot will usually arrive within this window anyway.
          console.error("Error triggering spin:", error);
          setTimeout(() => { isSpinningRef.current = false; }, 3000);
        });
      }

      // Stuck-spin recovery: if isSpinning has been true for over 30 seconds,
      // call triggerSpin so the server's stuck-spin recovery logic can reset it.
      // Without this, a crashed spin leaves isSpinning=true permanently and the
      // client never calls triggerSpin (deadlock).
      if (gameState.isSpinning && gameState.pendingRoundId && (now - gameState.pendingRoundId) > 30000 && !stuckSpinRecoveryFiredRef.current) {
        stuckSpinRecoveryFiredRef.current = true;
        console.warn('Stuck spin detected on client — calling triggerSpin for recovery');
        const triggerSpinFn = httpsCallable(functions, 'triggerSpin');
        triggerSpinFn().catch((error) => {
          console.error("Error triggering stuck-spin recovery:", error);
          stuckSpinRecoveryFiredRef.current = false; // allow retry
        });
      }
      // Reset recovery flag when spin completes normally
      if (!gameState.isSpinning) {
        stuckSpinRecoveryFiredRef.current = false;
      }
    }, 500);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // Helper functions for tooltip
  const showTooltip = (e, text, payout) => {
    const tooltip = document.createElement('div');
    tooltip.id = 'bet-tooltip';
    tooltip.innerHTML = `<strong>${text}</strong><br/>Pays ${payout}`;
    tooltip.style.cssText = 'position: fixed; background: rgba(0,0,0,0.95); color: #FFD700; padding: 10px 14px; border-radius: 8px; font-size: 13px; pointer-events: none; z-index: 10000; box-shadow: 0 4px 16px rgba(0,0,0,0.8); border: 2px solid #FFD700; font-family: Arial, sans-serif;';
    tooltip.style.left = e.clientX + 15 + 'px';
    tooltip.style.top = e.clientY + 15 + 'px';
    document.body.appendChild(tooltip);
  };

  const moveTooltip = (e) => {
    const tooltip = document.getElementById('bet-tooltip');
    if (tooltip) {
      tooltip.style.left = e.clientX + 15 + 'px';
      tooltip.style.top = e.clientY + 15 + 'px';
    }
  };

  const hideTooltip = () => {
    const tooltip = document.getElementById('bet-tooltip');
    if (tooltip) tooltip.remove();
  };

  // Helper function to render token chips on betting zones
  const renderTokenChip = (bet, position = 'center') => {
    const tokenImage = bet.tokenType === 'crewmate' ? '/CrewChip.png' : '/ImpostorChip.png';
    const positionStyles = {
      center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
      topLeft: { top: '5px', left: '5px' },
      topRight: { top: '5px', right: '5px' },
      bottomLeft: { bottom: '5px', left: '5px' },
      bottomRight: { bottom: '5px', right: '5px' }
    };

    return (
      <div style={{
        position: 'absolute',
        ...positionStyles[position],
        pointerEvents: 'none',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px'
      }}>
        <img
          src={tokenImage}
          alt={bet.tokenType}
          style={{
            width: '24px',
            height: '24px',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))'
          }}
        />
        <span style={{
          fontSize: '10px',
          fontWeight: 'bold',
          color: '#FFD700',
          textShadow: '0 1px 3px rgba(0,0,0,0.9)',
          background: 'rgba(0,0,0,0.7)',
          padding: '1px 4px',
          borderRadius: '3px',
          border: '1px solid #FFD700'
        }}>
          {parseFloat(bet.amount.toFixed(2))}
        </span>
      </div>
    );
  };

  function placeBet(betType, betValue) {
    if (isBettingLocked) {
      toast.warn("Betting is locked! Wait for the next round.");
      return;
    }

    // Optimistic local balance check (server will enforce authoritatively)
    if (bets.length === 0) {
      balanceAtRoundStartRef.current.crewmate = userData.crewmateTokens || 0;
      balanceAtRoundStartRef.current.imposter = userData.imposterTokens || 0;
    }
    const availableCrew = balanceAtRoundStartRef.current.crewmate - reservedTokensRef.current.crewmate;
    const availableImp  = balanceAtRoundStartRef.current.imposter  - reservedTokensRef.current.imposter;
    if (tokenType === "crewmate" && betAmount > availableCrew) { toast.warn("Insufficient crewmate tokens!"); return; }
    if (tokenType === "imposter" && betAmount > availableImp)  { toast.warn("Insufficient imposter tokens!");  return; }

    // Optimistic UI update
    const existingBetIndex = bets.findIndex(b => b.type === betType && b.tokenType === tokenType && JSON.stringify(b.value) === JSON.stringify(betValue));
    let newBets;
    if (existingBetIndex >= 0) {
      newBets = bets.map((b, i) => i === existingBetIndex ? { ...b, amount: parseFloat((b.amount + betAmount).toFixed(2)) } : b);
    } else {
      newBets = [...bets, { type: betType, value: betValue, amount: betAmount, tokenType }];
    }
    setBets(newBets);
    if (tokenType === "crewmate") reservedTokensRef.current.crewmate = parseFloat((reservedTokensRef.current.crewmate + betAmount).toFixed(2));
    else                          reservedTokensRef.current.imposter  = parseFloat((reservedTokensRef.current.imposter + betAmount).toFixed(2));
    // Instantly update Nav token display — frozen prevents auto-refresh from changing userData
    if (onTokenAdjustmentChange) onTokenAdjustmentChange({
      frozen: true,
      crewmate: reservedTokensRef.current.crewmate,
      imposter: reservedTokensRef.current.imposter,
    });

    // Send full canonical bet list to server — server deducts tokens and stores bets
    const displayName = userData?.ign || user?.email?.split('@')[0] || 'Player';
    const mySequence = ++betSequenceRef.current;
    const fn = httpsCallable(functions, 'placeBets');
    fn({ bets: newBets, displayName }).catch((e) => {
      // Only roll back if no newer bet has been placed since this one.
      // Later calls send the full bet list, so they supersede this one on the server.
      if (betSequenceRef.current === mySequence) {
        setBets(prev => {
          // Remove this specific bet (or reduce its amount) instead of clobbering with stale state
          const idx = prev.findIndex(b => b.type === betType && b.value === betValue && b.tokenType === tokenType);
          if (idx < 0) return prev;
          if (prev[idx].amount <= betAmount) return prev.filter((_, i) => i !== idx);
          return prev.map((b, i) => i === idx ? { ...b, amount: parseFloat((b.amount - betAmount).toFixed(2)) } : b);
        });
        if (tokenType === "crewmate") reservedTokensRef.current.crewmate = parseFloat((reservedTokensRef.current.crewmate - betAmount).toFixed(2));
        else                          reservedTokensRef.current.imposter  = parseFloat((reservedTokensRef.current.imposter - betAmount).toFixed(2));
        if (onTokenAdjustmentChange) onTokenAdjustmentChange({
          frozen: true,
          crewmate: reservedTokensRef.current.crewmate,
          imposter: reservedTokensRef.current.imposter,
        });
      }
      if (e.message && e.message.includes('Betting is locked')) {
        toast.warn("Bet not placed — betting already locked.");
        return;
      }
      console.error("Failed to place bet:", e);
      toast.error("Failed to place bet: " + (e.message || "Unknown error"));
    });
  }

  function clearBets() {
    if (isBettingLocked) { toast.warn("Cannot clear bets now! Betting is locked."); return; }
    if (clearingBetsRef.current) return;
    if (bets.length === 0) return;
    clearingBetsRef.current = true;

    // Save previous state so we can restore on server error
    const prevBets = bets;
    const prevReserved = { ...reservedTokensRef.current };
    const prevBalance = { ...balanceAtRoundStartRef.current };

    // Optimistic UI clear
    reservedTokensRef.current = { crewmate: 0, imposter: 0 };
    balanceAtRoundStartRef.current = { crewmate: 0, imposter: 0 };
    betSequenceRef.current = 0;
    betsClearedRef.current = true;
    setBets([]);
    // Unfreeze nav — server will refund tokens, so let userData be source of truth again
    if (onTokenAdjustmentChange) onTokenAdjustmentChange({ frozen: false, crewmate: 0, imposter: 0 });

    const fn = httpsCallable(functions, 'clearBets');
    fn().then(() => {
      // Sync server balance after clear completes
      onUserDataChange();
    }).catch((e) => {
      console.error("Failed to clear bets:", e);
      // Server rejected the clear — bets are still active on the server.
      // Restore local state so user sees their bets and gets the payout notification.
      betsClearedRef.current = false;
      reservedTokensRef.current = prevReserved;
      balanceAtRoundStartRef.current = prevBalance;
      setBets(prevBets);
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({
        frozen: true,
        crewmate: prevReserved.crewmate,
        imposter: prevReserved.imposter,
      });
      toast.warn("Could not clear bets — betting already locked. Your bets are still active.");
    });
    clearingBetsRef.current = false;
  }

  const totalBetAmount = parseFloat(bets.reduce((sum, b) => sum + b.amount, 0).toFixed(2));
  const localTotalCrew = parseFloat(bets.filter(b => b.tokenType === 'crewmate').reduce((s, b) => s + b.amount, 0).toFixed(2));
  const localTotalImp  = parseFloat(bets.filter(b => b.tokenType === 'imposter').reduce((s, b) => s + b.amount, 0).toFixed(2));
  const isBettingLocked = timeUntilSpin <= 5 || isSpinning;

  // Consolidate duplicate bets (same type + value + tokenType) into one entry
  const consolidatedBets = Object.values(
    bets.reduce((acc, bet) => {
      const key = `${bet.type}|${JSON.stringify(bet.value)}|${bet.tokenType}`;
      if (acc[key]) {
        acc[key] = { ...acc[key], amount: acc[key].amount + bet.amount };
      } else {
        acc[key] = { ...bet };
      }
      return acc;
    }, {})
  );

  return (
    <div style={{
      position: isMobile ? 'relative' : 'fixed',
      top: isMobile ? 0 : 0,
      left: isMobile ? undefined : 'var(--sidebar-w)',
      right: isMobile ? undefined : 0,
      bottom: isMobile ? undefined : 0,
      overflow: isMobile ? 'auto' : 'hidden',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      minHeight: isMobile ? 'calc(100vh - 48px - 60px)' : undefined,
      paddingBottom: isMobile ? '120px' : 0,
    }}>

      {/* Win/Loss Notification */}
      {winNotification && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: isMobile ? '20px 24px' : '40px 60px',
          maxWidth: isMobile ? '90vw' : undefined,
          background: winNotification.won
            ? 'linear-gradient(145deg, #228B22 0%, #006400 100%)'
            : 'linear-gradient(145deg, #DC143C 0%, #8B0000 100%)',
          border: `4px solid ${winNotification.won ? '#32CD32' : '#FF6B6B'}`,
          borderRadius: '20px',
          zIndex: 1000,
          boxShadow: '0 10px 50px rgba(0,0,0,0.8)',
          textAlign: 'center',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'white', marginBottom: '12px' }}>
            {winNotification.won ? 'YOU WON!' : 'YOU LOST'}
          </div>
          {(winNotification.lines || []).map((line, i) => {
            const icon = line.type === 'crewmate' ? '🔵' : '🔴';
            const sign = line.net >= 0 ? '+' : '';
            const color = line.net >= 0 ? '#4ade80' : '#f87171';
            return (
              <div key={i} style={{ fontSize: '24px', fontWeight: 'bold', color, marginBottom: '4px' }}>
                {icon} {sign}{parseFloat(line.net.toFixed(2))} tokens
              </div>
            );
          })}
          {(!winNotification.lines || winNotification.lines.length === 0) && (
            <div style={{ fontSize: '30px', color: '#FFD700', fontWeight: 'bold' }}>
              {winNotification.won ? '+' : '-'}{parseFloat(winNotification.amount.toFixed(2))} tokens
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { transform: translate(-50%, -60%); opacity: 0; }
          to { transform: translate(-50%, -50%); opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* MAIN LAYOUT: Shelf + right content side by side, both fill fixed container */}

        {/* ── LEFT SHELF (hidden on mobile — controls move to bottom bar) ── */}
        <div style={{
          width: 'clamp(260px, 20vw, 320px)',
          flexShrink: 0,
          height: '100%',
          overflowY: 'auto',
          display: isMobile ? 'none' : 'flex',
          flexDirection: 'column',
          gap: '10px',
          padding: '14px',
          background: 'linear-gradient(180deg, rgba(15,15,15,0.98) 0%, rgba(20,20,20,0.98) 100%)',
          borderRight: '2px solid rgba(255,215,0,0.2)',
          boxSizing: 'border-box'
        }}>

          {/* Back to Casino */}
          <button
            onClick={onBack}
            style={{
              width: '100%',
              padding: '10px 16px',
              cursor: 'pointer',
              background: 'rgba(40,40,40,0.8)',
              color: 'white',
              border: '2px solid rgba(100,100,100,0.5)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.2s',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(60,60,60,0.9)';
              e.target.style.borderColor = 'rgba(150,150,150,0.7)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(40,40,40,0.8)';
              e.target.style.borderColor = 'rgba(100,100,100,0.5)';
            }}
          >
            ← Back to Casino
          </button>

          {/* Timer */}
          <div style={{
            padding: '12px 16px',
            background: isBettingLocked ? 'rgba(220, 20, 60, 0.15)' : 'rgba(34, 139, 34, 0.15)',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            border: `2px solid ${isBettingLocked ? '#DC143C' : '#228B22'}`,
            textAlign: 'center'
          }}>
            {isSpinning ? '🌀 SPINNING...' : isBettingLocked
              ? `🔒 LOCKED — ${timeUntilSpin}s`
              : `✅ PLACE BETS — ${timeUntilSpin}s`}
          </div>

          {/* Token Type */}
          <div style={{
            background: 'linear-gradient(145deg, rgba(40,40,40,0.8) 0%, rgba(20,20,20,0.9) 100%)',
            padding: '14px',
            borderRadius: '10px',
            border: '2px solid rgba(255,215,0,0.3)'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '8px' }}>TOKEN TYPE</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setTokenType("crewmate")} style={{
                flex: 1, padding: '10px',
                background: tokenType === "crewmate" ? 'linear-gradient(145deg, #4169E1 0%, #1E3A8A 100%)' : 'rgba(50,50,50,0.5)',
                color: 'white', border: tokenType === "crewmate" ? '2px solid #6495ED' : '2px solid rgba(100,100,100,0.3)',
                borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', transition: 'all 0.2s'
              }}>🔵 Crew</button>
              <button onClick={() => setTokenType("imposter")} style={{
                flex: 1, padding: '10px',
                background: tokenType === "imposter" ? 'linear-gradient(145deg, #DC143C 0%, #8B0000 100%)' : 'rgba(50,50,50,0.5)',
                color: 'white', border: tokenType === "imposter" ? '2px solid #FF6B6B' : '2px solid rgba(100,100,100,0.3)',
                borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', transition: 'all 0.2s'
              }}>🔴 Imp</button>
            </div>
          </div>

          {/* Bet Amount */}
          <div style={{
            background: 'linear-gradient(145deg, rgba(40,40,40,0.8) 0%, rgba(20,20,20,0.9) 100%)',
            padding: '14px',
            borderRadius: '10px',
            border: '2px solid rgba(255,215,0,0.3)'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '8px' }}>BET AMOUNT</div>
            <div style={{ position: 'relative', marginBottom: '10px' }}>
              <input
                type="number" min="0.01" step="0.01" value={betAmount}
                onChange={(e) => setBetAmount(Math.max(0.01, parseFloat(e.target.value) || 0.01))}
                style={{
                  width: '100%', padding: '10px 40px 10px 12px', boxSizing: 'border-box',
                  background: 'rgba(30,30,30,0.8)', border: '2px solid rgba(255,215,0,0.3)',
                  borderRadius: '8px', color: 'white', fontSize: '16px', fontWeight: 'bold', outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FFD700'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255,215,0,0.3)'}
              />
              <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px' }}>
                {tokenType === "crewmate" ? "🔵" : "🔴"}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
              {[0.1, 0.5, 1, 5, 10, 25].map(amount => (
                <button key={amount} onClick={() => setBetAmount(amount)} style={{
                  padding: '8px 4px',
                  background: betAmount === amount ? 'rgba(255,215,0,0.2)' : 'rgba(50,50,50,0.5)',
                  border: betAmount === amount ? '2px solid #FFD700' : '2px solid rgba(100,100,100,0.3)',
                  borderRadius: '6px', color: betAmount === amount ? '#FFD700' : 'var(--text-secondary)',
                  fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s'
                }}>{amount}</button>
              ))}
              <button onClick={() => {
                const available = tokenType === 'crewmate'
                  ? (balanceAtRoundStartRef.current.crewmate || userData?.crewmateTokens || 0) - reservedTokensRef.current.crewmate
                  : (balanceAtRoundStartRef.current.imposter || userData?.imposterTokens || 0) - reservedTokensRef.current.imposter;
                setBetAmount(parseFloat(Math.max(0.01, available).toFixed(2)));
              }} style={{
                padding: '8px 4px',
                background: 'rgba(232,56,74,0.2)',
                border: '2px solid rgba(232,56,74,0.4)',
                borderRadius: '6px', color: '#E8384A',
                fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s',
                gridColumn: 'span 2'
              }}>ALL IN</button>
            </div>
          </div>

          {/* Your Bets */}
          <div style={{
            background: 'linear-gradient(145deg, rgba(40,40,40,0.8) 0%, rgba(20,20,20,0.9) 100%)',
            padding: '14px',
            borderRadius: '10px',
            border: '2px solid rgba(255,215,0,0.3)'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '10px' }}>📋 YOUR BETS</div>
            {bets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-secondary)', fontSize: '12px', opacity: 0.6 }}>
                No bets placed yet
              </div>
            ) : (
              <div style={{ maxHeight: '160px', overflowY: 'auto', marginBottom: '10px' }}>
                {consolidatedBets.map((bet, i) => (
                  <div key={i} style={{
                    padding: '8px 10px', background: 'rgba(50,50,50,0.5)', borderRadius: '6px',
                    marginBottom: '5px', border: '1px solid rgba(100,100,100,0.3)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '11px', fontWeight: '500' }}>
                      {bet.type === "straight" ? `#${bet.value}` :
                       bet.type === "split" ? `Split: ${bet.value.join('/')}` :
                       bet.type === "street" ? `Street: ${bet.value[0]}-${bet.value[2]}` :
                       bet.type === "corner" ? `Corner: ${bet.value.length} nums` :
                       bet.type === "sixline" ? `Six-Line: ${bet.value[0]}-${bet.value[5]}` :
                       bet.type === "column" ? `Column ${bet.value}` :
                       bet.type === "dozen" ? `${bet.value === 1 ? '1st' : bet.value === 2 ? '2nd' : '3rd'} 12` :
                       bet.type === "range" ? bet.value : bet.type.toUpperCase()}
                    </span>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#FFD700' }}>
                      {parseFloat(bet.amount.toFixed(2))} {bet.tokenType === "crewmate" ? "🔵" : "🔴"}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div style={{
              padding: '8px', background: 'rgba(255,215,0,0.1)', borderRadius: '6px',
              border: '1px solid rgba(255,215,0,0.3)', textAlign: 'center', marginBottom: '10px'
            }}>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '2px' }}>TOTAL WAGERED</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#FFD700' }}>{totalBetAmount} tokens</div>
            </div>
            <button onClick={clearBets} disabled={isBettingLocked || bets.length === 0} style={{
              width: '100%', padding: '10px',
              background: isBettingLocked || bets.length === 0 ? 'rgba(100,100,100,0.3)' : 'linear-gradient(145deg, #DC143C 0%, #8B0000 100%)',
              color: 'white', border: '2px solid ' + (isBettingLocked || bets.length === 0 ? 'rgba(100,100,100,0.3)' : '#FF6B6B'),
              borderRadius: '8px', cursor: isBettingLocked || bets.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: 'bold', transition: 'all 0.2s'
            }}>🗑️ Clear All Bets</button>
          </div>

          {/* Active Bets */}
          <div style={{
            background: 'linear-gradient(145deg, rgba(40,40,40,0.8) 0%, rgba(20,20,20,0.9) 100%)',
            padding: '14px', borderRadius: '10px', border: '2px solid rgba(255,215,0,0.3)'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '10px' }}>🎰 ACTIVE BETS</div>
            {recentPlayerBets.length === 0 && totalBetAmount === 0 ? (
              <div style={{ textAlign: 'center', padding: '10px 0', color: 'var(--text-secondary)', fontSize: '12px', opacity: 0.6 }}>
                No active bets this round
              </div>
            ) : (
              <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                {recentPlayerBets.map((pb, i) => {
                  const isMe = user?.uid && pb.uid === user.uid;
                  const displayCrew = isMe ? localTotalCrew : pb.totalCrew;
                  const displayImp  = isMe ? localTotalImp  : pb.totalImp;
                  const displayBets = isMe ? bets : (pb.bets || []);
                  return (
                    <div key={pb.uid || i} style={{
                      padding: '7px 10px', background: isMe ? 'rgba(255,215,0,0.08)' : 'rgba(50,50,50,0.5)',
                      borderRadius: '6px', marginBottom: '5px',
                      border: isMe ? '1px solid rgba(255,215,0,0.3)' : '1px solid rgba(100,100,100,0.3)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', fontWeight: '500', color: isMe ? '#FFD700' : 'inherit' }}>
                          {pb.name}
                        </span>
                        <span style={{ fontSize: '11px', fontWeight: 'bold', display: 'flex', gap: '6px' }}>
                          {displayCrew > 0 && <span style={{ color: '#6EC6FF' }}>{parseFloat(displayCrew.toFixed(2))}🔵</span>}
                          {displayImp  > 0 && <span style={{ color: '#FF6B6B' }}>{parseFloat(displayImp.toFixed(2))}🔴</span>}
                        </span>
                      </div>
                      {displayBets.length > 0 && (
                        <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                          {displayBets.map((b, j) => (
                            <span key={j} style={{
                              fontSize: '9px', padding: '1px 5px', borderRadius: '3px',
                              background: b.type === 'red' ? 'rgba(220,20,60,0.3)' :
                                          b.type === 'black' ? 'rgba(80,80,80,0.5)' :
                                          b.type === 'green' ? 'rgba(0,128,0,0.3)' :
                                          'rgba(255,255,255,0.08)',
                              border: '1px solid rgba(255,255,255,0.15)',
                              color: 'rgba(255,255,255,0.8)',
                            }}>
                              {b.type === "straight" ? `#${b.value}` :
                               b.type === "split" ? `${Array.isArray(b.value) ? b.value.join('/') : b.value}` :
                               b.type === "street" ? `St ${Array.isArray(b.value) ? b.value[0] + '-' + b.value[2] : b.value}` :
                               b.type === "corner" ? `Cor ${Array.isArray(b.value) ? b.value.length : ''}` :
                               b.type === "sixline" ? `6L ${Array.isArray(b.value) ? b.value[0] + '-' + b.value[5] : b.value}` :
                               b.type === "column" ? `Col ${b.value}` :
                               b.type === "dozen" ? `${b.value === 1 ? '1st' : b.value === 2 ? '2nd' : '3rd'} 12` :
                               b.type === "range" ? b.value : b.type.toUpperCase()}
                              {' '}{parseFloat(b.amount.toFixed(2))}{b.tokenType === 'crewmate' ? '🔵' : '🔴'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Show local bets immediately even before Firestore snapshot arrives */}
                {totalBetAmount > 0 && user?.uid && !recentPlayerBets.some(pb => pb.uid === user.uid) && (
                  <div style={{
                    padding: '7px 10px', background: 'rgba(255,215,0,0.08)',
                    borderRadius: '6px', marginBottom: '5px',
                    border: '1px solid rgba(255,215,0,0.3)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', fontWeight: '500', color: '#FFD700' }}>
                        {userData?.ign || user?.email?.split('@')[0] || 'You'}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', display: 'flex', gap: '6px' }}>
                        {localTotalCrew > 0 && <span style={{ color: '#6EC6FF' }}>{localTotalCrew}🔵</span>}
                        {localTotalImp  > 0 && <span style={{ color: '#FF6B6B' }}>{localTotalImp}🔴</span>}
                      </span>
                    </div>
                    {bets.length > 0 && (
                      <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                        {bets.map((b, j) => (
                          <span key={j} style={{
                            fontSize: '9px', padding: '1px 5px', borderRadius: '3px',
                            background: b.type === 'red' ? 'rgba(220,20,60,0.3)' :
                                        b.type === 'black' ? 'rgba(80,80,80,0.5)' :
                                        b.type === 'green' ? 'rgba(0,128,0,0.3)' :
                                        'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            color: 'rgba(255,255,255,0.8)',
                          }}>
                            {b.type === "straight" ? `#${b.value}` :
                             b.type === "split" ? `${Array.isArray(b.value) ? b.value.join('/') : b.value}` :
                             b.type === "street" ? `St ${Array.isArray(b.value) ? b.value[0] + '-' + b.value[2] : b.value}` :
                             b.type === "corner" ? `Cor ${Array.isArray(b.value) ? b.value.length : ''}` :
                             b.type === "sixline" ? `6L ${Array.isArray(b.value) ? b.value[0] + '-' + b.value[5] : b.value}` :
                             b.type === "column" ? `Col ${b.value}` :
                             b.type === "dozen" ? `${b.value === 1 ? '1st' : b.value === 2 ? '2nd' : '3rd'} 12` :
                             b.type === "range" ? b.value : b.type.toUpperCase()}
                            {' '}{parseFloat(b.amount.toFixed(2))}{b.tokenType === 'crewmate' ? '🔵' : '🔴'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div style={{
              padding: '7px', background: 'rgba(255,215,0,0.1)', borderRadius: '6px',
              border: '1px solid rgba(255,215,0,0.3)', textAlign: 'center', marginTop: '8px'
            }}>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '2px' }}>TOTAL WAGERED THIS ROUND</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#FFD700' }}>
                {(() => {
                  // Always use local totals for current user, server totals for others
                  const othersTotal = recentPlayerBets
                    .filter(pb => pb.uid !== user?.uid)
                    .reduce((s, pb) => s + pb.total, 0);
                  return othersTotal + totalBetAmount;
                })()} tokens
              </div>
            </div>
          </div>

          {/* Recent Spins */}
          <div style={{
            background: 'linear-gradient(145deg, rgba(40,40,40,0.8) 0%, rgba(20,20,20,0.9) 100%)',
            padding: '14px',
            borderRadius: '10px',
            border: '2px solid rgba(255,215,0,0.3)'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '10px' }}>🕐 RECENT SPINS</div>
            {history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-secondary)', fontSize: '12px', opacity: 0.6 }}>
                No spins yet
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxHeight: '160px', overflowY: 'auto' }}>
                {history.map((h, i) => (
                  <div key={i} style={{
                    width: '28px', height: '28px',
                    background: h.color === "red" ? "linear-gradient(145deg, #DC143C 0%, #8B0000 100%)"
                      : h.color === "black" ? "linear-gradient(145deg, #1a1a1a 0%, #000 100%)"
                      : "linear-gradient(145deg, #228B22 0%, #006400 100%)",
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '50%', fontSize: '10px', fontWeight: 'bold',
                    border: `2px solid ${h.color === 'red' ? '#FF6B6B' : h.color === 'black' ? '#444' : '#32CD32'}`,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                  }}>{h.num}</div>
                ))}
              </div>
            )}
          </div>
        </div>



        {/* ── RIGHT CONTENT: Wheel on top, table below ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0', padding: '0', overflowY: 'auto', height: isMobile ? 'auto' : '100%', boxSizing: 'border-box', minWidth: 0 }}>

          {/* Wheel — shows on both desktop and mobile */}
          <div style={{
            flex: isMobile ? undefined : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: isMobile ? undefined : '300px',
            height: isMobile ? '280px' : undefined,
            overflow: 'hidden',
            paddingRight: isMobile ? 0 : '16px',
            margin: isMobile ? '8px 0 6px' : undefined,
            flexShrink: 0,
          }}>
            {isSpinning || result ? (
              <RouletteWheel numbers={numbers} result={result} isSpinning={isSpinning} />
            ) : (
              <div style={{
                padding: isMobile ? '20px' : '60px', borderRadius: '16px', background: 'rgba(50,50,50,0.5)',
                border: '4px dashed #666', textAlign: 'center', width: '100%', maxWidth: '500px'
              }}>
                <div style={{ fontSize: isMobile ? '28px' : '48px', marginBottom: '15px', opacity: 0.5 }}>🎲</div>
                <div style={{ fontSize: isMobile ? '13px' : '20px', color: 'var(--text-secondary)' }}>Waiting for first spin...</div>
              </div>
            )}
          </div>

          {/* Mobile: compact status + history bar below wheel */}
          {isMobile && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 10px', margin: '0 0 8px',
              background: 'rgba(20,20,20,0.9)', borderRadius: '8px',
              border: '1px solid rgba(255,215,0,0.2)',
            }}>
              <div style={{
                fontSize: '12px', fontWeight: '700',
                color: isBettingLocked ? '#DC143C' : '#32CD32',
              }}>
                {isSpinning ? '🌀 SPINNING' : isBettingLocked ? `🔒 LOCKED ${timeUntilSpin}s` : `✅ BETS OPEN ${timeUntilSpin}s`}
              </div>
              {history.length > 0 && (
                <div style={{ display: 'flex', gap: '3px' }}>
                  {history.slice(0, 8).map((h, i) => (
                    <div key={i} style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: h.color === 'red' ? '#DC143C' : h.color === 'black' ? '#1a1a1a' : '#228B22',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '8px', fontWeight: 'bold',
                      border: `1.5px solid ${h.color === 'red' ? '#FF6B6B' : h.color === 'black' ? '#444' : '#32CD32'}`,
                    }}>{h.num}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Roulette Table */}
          <div style={{ marginBottom: '0', paddingRight: isMobile ? '0' : '16px' }}>
        <div style={{
          background: 'rgba(30,30,30,0.6)',
          padding: isMobile ? '8px' : '20px',
          borderRadius: '12px',
          border: '1px solid rgba(100,100,100,0.3)',
          overflowX: isMobile ? 'auto' : undefined,
          WebkitOverflowScrolling: 'touch',
        }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Main table area with 0/00 and number grid */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {/* 0 and 00 column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' }}>
                  <button
                    className="roulette-number-btn"
                    onClick={() => placeBet("straight", "00")}
                    disabled={isBettingLocked}
                    onMouseEnter={e => {
                      if (!isBettingLocked) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                        showTooltip(e, '00 Straight', '35:1');
                      }
                    }}
                    onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                      hideTooltip();
                    }}
                    style={{
                      padding: isMobile ? '14px 8px' : '28px 18px',
                      background: isBettingLocked ? 'rgba(34,139,34,0.3)' : 'linear-gradient(145deg, #228B22 0%, #006400 100%)',
                      color: 'white',
                      border: '2px solid #32CD32',
                      borderRadius: '6px',
                      cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      boxShadow: isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)',
                      transition: 'all 0.2s',
                      minWidth: isMobile ? '36px' : '55px',
                      position: 'relative'
                    }}
                  >
                    00
                    {bets.filter(b => b.type === 'straight' && b.value === '00').map((bet, idx) => (
                      <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                    ))}
                  </button>
                  <button
                    className="roulette-number-btn"
                    onClick={() => placeBet("straight", "0")}
                    disabled={isBettingLocked}
                    onMouseEnter={e => {
                      if (!isBettingLocked) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                        showTooltip(e, '0 Straight', '35:1');
                      }
                    }}
                    onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                      hideTooltip();
                    }}
                    style={{
                      padding: isMobile ? '14px 8px' : '28px 18px',
                      background: isBettingLocked ? 'rgba(34,139,34,0.3)' : 'linear-gradient(145deg, #228B22 0%, #006400 100%)',
                      color: 'white',
                      border: '2px solid #32CD32',
                      borderRadius: '6px',
                      cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      boxShadow: isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)',
                      transition: 'all 0.2s',
                      minWidth: isMobile ? '36px' : '55px',
                      position: 'relative'
                    }}
                  >
                    0
                    {bets.filter(b => b.type === 'straight' && b.value === "0").map((bet, idx) => (
                      <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                    ))}
                  </button>

                </div>

                {/* Numbers grid container with overlaid betting zones */}
                <div style={{ position: 'relative', flex: 1 }}>
                  {/* Main number grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(6, 1fr)' : 'repeat(12, 1fr)', gridTemplateRows: isMobile ? 'repeat(6, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? '4px' : '8px' }}>
                    {/* Row 1: 3,6,9,12,15,18,21,24,27,30,33,36 */}
                    {[3,6,9,12,15,18,21,24,27,30,33,36].map(num => {
                      const numObj = numbers.find(n => n.num === num);
                      return (
                        <button
                          className="roulette-number-btn"
                          key={num}
                          onClick={() => placeBet("straight", num)}
                          disabled={isBettingLocked}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              e.currentTarget.style.transform = 'scale(1.05)';
                              e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                              showTooltip(e, `${num} Straight`, '35:1');
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            hideTooltip();
                          }}
                          style={{
                            padding: isMobile ? '12px 4px' : '22px 10px',
                            background: isBettingLocked
                              ? `rgba(${numObj.color === 'red' ? '220,20,60' : '0,0,0'}, 0.3)`
                              : numObj.color === "red"
                              ? "linear-gradient(145deg, #DC143C 0%, #8B0000 100%)"
                              : "linear-gradient(145deg, #1a1a1a 0%, #000 100%)",
                            color: 'white',
                            border: `2px solid ${numObj.color === 'red' ? '#FF6B6B' : '#444'}`,
                            borderRadius: '6px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            fontSize: isMobile ? '12px' : '16px',
                            fontWeight: 'bold',
                            boxShadow: isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)',
                            transition: 'all 0.2s',
                            position: 'relative'
                          }}
                        >
                          {num}
                          {bets.filter(b => b.type === 'straight' && b.value === num).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </button>
                      );
                    })}
                    {/* Row 2: 2,5,8,11,14,17,20,23,26,29,32,35 */}
                    {[2,5,8,11,14,17,20,23,26,29,32,35].map(num => {
                      const numObj = numbers.find(n => n.num === num);
                      return (
                        <button
                          className="roulette-number-btn"
                          key={num}
                          onClick={() => placeBet("straight", num)}
                          disabled={isBettingLocked}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              e.currentTarget.style.transform = 'scale(1.05)';
                              e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                              showTooltip(e, `${num} Straight`, '35:1');
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            hideTooltip();
                          }}
                          style={{
                            padding: isMobile ? '12px 4px' : '22px 10px',
                            background: isBettingLocked
                              ? `rgba(${numObj.color === 'red' ? '220,20,60' : '0,0,0'}, 0.3)`
                              : numObj.color === "red"
                              ? "linear-gradient(145deg, #DC143C 0%, #8B0000 100%)"
                              : "linear-gradient(145deg, #1a1a1a 0%, #000 100%)",
                            color: 'white',
                            border: `2px solid ${numObj.color === 'red' ? '#FF6B6B' : '#444'}`,
                            borderRadius: '6px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            fontSize: isMobile ? '12px' : '16px',
                            fontWeight: 'bold',
                            boxShadow: isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)',
                            transition: 'all 0.2s',
                            position: 'relative'
                          }}
                        >
                          {num}
                          {bets.filter(b => b.type === 'straight' && b.value === num).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </button>
                      );
                    })}
                    {/* Row 3: 1,4,7,10,13,16,19,22,25,28,31,34 */}
                    {[1,4,7,10,13,16,19,22,25,28,31,34].map(num => {
                      const numObj = numbers.find(n => n.num === num);
                      return (
                        <button
                          className="roulette-number-btn"
                          key={num}
                          onClick={() => placeBet("straight", num)}
                          disabled={isBettingLocked}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              e.currentTarget.style.transform = 'scale(1.05)';
                              e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                              showTooltip(e, `${num} Straight`, '35:1');
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            hideTooltip();
                          }}
                          style={{
                            padding: isMobile ? '12px 4px' : '22px 10px',
                            background: isBettingLocked
                              ? `rgba(${numObj.color === 'red' ? '220,20,60' : '0,0,0'}, 0.3)`
                              : numObj.color === "red"
                              ? "linear-gradient(145deg, #DC143C 0%, #8B0000 100%)"
                              : "linear-gradient(145deg, #1a1a1a 0%, #000 100%)",
                            color: 'white',
                            border: `2px solid ${numObj.color === 'red' ? '#FF6B6B' : '#444'}`,
                            borderRadius: '6px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            fontSize: isMobile ? '12px' : '16px',
                            fontWeight: 'bold',
                            boxShadow: isBettingLocked ? 'none' : '0 2px 8px rgba(0,0,0,0.3)',
                            transition: 'all 0.2s',
                            position: 'relative'
                          }}
                        >
                          {num}
                          {bets.filter(b => b.type === 'straight' && b.value === num).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </button>
                      );
                    })}
                  </div>

                  {/* Split/Corner/Street/Sixline overlay zones - hidden on mobile (grid layout differs) */}
                  {!isMobile && <>
                  {/* Split betting zones - horizontal splits between columns (invisible with tooltip) */}
                  {[...Array(11)].map((_, colIdx) => {
                    const col = colIdx;
                    const row1Nums = [3,6,9,12,15,18,21,24,27,30,33,36];
                    const row2Nums = [2,5,8,11,14,17,20,23,26,29,32,35];
                    const row3Nums = [1,4,7,10,13,16,19,22,25,28,31,34];

                    return (
                      <React.Fragment key={`vsplit-${col}`}>
                        {/* Split between row 1 numbers */}
                        <div
                          id={`vsplit-r1-${col}`}
                          onClick={() => !isBettingLocked && placeBet("split", [row1Nums[col], row1Nums[col+1]])}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              showTooltip(e, `${row1Nums[col]}/${row1Nums[col+1]} Split`, '17:1');
                              const buttons = document.querySelectorAll('.roulette-number-btn');
                              buttons.forEach(btn => {
                                const btnNum = btn.textContent.trim();
                                if ([row1Nums[col], row1Nums[col+1]].some(n => n == btnNum || n === btnNum)) {
                                  btn.style.transform = 'scale(1.05)';
                                  btn.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                                }
                              });
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            hideTooltip();
                            const buttons = document.querySelectorAll('.roulette-number-btn');
                            buttons.forEach(btn => {
                              btn.style.transform = 'scale(1)';
                              btn.style.boxShadow = btn.disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            });
                          }}
                          style={{
                            position: 'absolute',
                            top: 0,
                            bottom: 'calc(66.67% - 2px)',
                            left: `calc(${((col + 1) / 12) * 100}% - 4px)`,
                            width: '8px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            zIndex: 10,
                            pointerEvents: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {bets.filter(b => b.type === 'split' && JSON.stringify(b.value.sort()) === JSON.stringify([row1Nums[col], row1Nums[col+1]].sort())).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </div>
                        {/* Split between row 2 numbers */}
                        <div
                          id={`vsplit-r2-${col}`}
                          onClick={() => !isBettingLocked && placeBet("split", [row2Nums[col], row2Nums[col+1]])}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              showTooltip(e, `${row2Nums[col]}/${row2Nums[col+1]} Split`, '17:1');
                              const buttons = document.querySelectorAll('.roulette-number-btn');
                              buttons.forEach(btn => {
                                const btnNum = btn.textContent.trim();
                                if ([row2Nums[col], row2Nums[col+1]].some(n => n == btnNum || n === btnNum)) {
                                  btn.style.transform = 'scale(1.05)';
                                  btn.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                                }
                              });
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            hideTooltip();
                            const buttons = document.querySelectorAll('.roulette-number-btn');
                            buttons.forEach(btn => {
                              btn.style.transform = 'scale(1)';
                              btn.style.boxShadow = btn.disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            });
                          }}
                          style={{
                            position: 'absolute',
                            top: 'calc(33.33% + 2px)',
                            bottom: 'calc(33.33% - 2px)',
                            left: `calc(${((col + 1) / 12) * 100}% - 4px)`,
                            width: '8px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            zIndex: 10,
                            pointerEvents: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {bets.filter(b => b.type === 'split' && JSON.stringify(b.value.sort()) === JSON.stringify([row2Nums[col], row2Nums[col+1]].sort())).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </div>
                        {/* Split between row 3 numbers */}
                        <div
                          id={`vsplit-r3-${col}`}
                          onClick={() => !isBettingLocked && placeBet("split", [row3Nums[col], row3Nums[col+1]])}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              showTooltip(e, `${row3Nums[col]}/${row3Nums[col+1]} Split`, '17:1');
                              const buttons = document.querySelectorAll('.roulette-number-btn');
                              buttons.forEach(btn => {
                                const btnNum = btn.textContent.trim();
                                if ([row3Nums[col], row3Nums[col+1]].some(n => n == btnNum || n === btnNum)) {
                                  btn.style.transform = 'scale(1.05)';
                                  btn.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                                }
                              });
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            hideTooltip();
                            const buttons = document.querySelectorAll('.roulette-number-btn');
                            buttons.forEach(btn => {
                              btn.style.transform = 'scale(1)';
                              btn.style.boxShadow = btn.disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            });
                          }}
                          style={{
                            position: 'absolute',
                            top: 'calc(66.67% + 2px)',
                            bottom: 0,
                            left: `calc(${((col + 1) / 12) * 100}% - 4px)`,
                            width: '8px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            zIndex: 10,
                            pointerEvents: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {bets.filter(b => b.type === 'split' && JSON.stringify(b.value.sort()) === JSON.stringify([row3Nums[col], row3Nums[col+1]].sort())).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {/* Split betting zones - vertical splits between rows (invisible with tooltip) */}
                  {[...Array(12)].map((_, colIdx) => {
                    const row1Nums = [3,6,9,12,15,18,21,24,27,30,33,36];
                    const row2Nums = [2,5,8,11,14,17,20,23,26,29,32,35];
                    const row3Nums = [1,4,7,10,13,16,19,22,25,28,31,34];

                    return (
                      <React.Fragment key={`hsplit-${colIdx}`}>
                        {/* Split between row 1 and row 2 */}
                        <div
                          id={`hsplit-r1r2-${colIdx}`}
                          onClick={() => !isBettingLocked && placeBet("split", [row1Nums[colIdx], row2Nums[colIdx]])}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              showTooltip(e, `${row1Nums[colIdx]}/${row2Nums[colIdx]} Split`, '17:1');
                              const buttons = document.querySelectorAll('.roulette-number-btn');
                              buttons.forEach(btn => {
                                const btnNum = btn.textContent.trim();
                                if ([row1Nums[colIdx], row2Nums[colIdx]].some(n => n == btnNum || n === btnNum)) {
                                  btn.style.transform = 'scale(1.05)';
                                  btn.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                                }
                              });
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            hideTooltip();
                            const buttons = document.querySelectorAll('.roulette-number-btn');
                            buttons.forEach(btn => {
                              btn.style.transform = 'scale(1)';
                              btn.style.boxShadow = btn.disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            });
                          }}
                          style={{
                            position: 'absolute',
                            top: 'calc(33.33% - 4px)',
                            left: `calc(${(colIdx / 12) * 100}% + 2px)`,
                            right: `calc(${((11 - colIdx) / 12) * 100}% + 2px)`,
                            height: '8px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            zIndex: 10,
                            pointerEvents: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {bets.filter(b => b.type === 'split' && JSON.stringify(b.value.sort()) === JSON.stringify([row1Nums[colIdx], row2Nums[colIdx]].sort())).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </div>
                        {/* Split between row 2 and row 3 */}
                        <div
                          id={`hsplit-r2r3-${colIdx}`}
                          onClick={() => !isBettingLocked && placeBet("split", [row2Nums[colIdx], row3Nums[colIdx]])}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              showTooltip(e, `${row2Nums[colIdx]}/${row3Nums[colIdx]} Split`, '17:1');
                              const buttons = document.querySelectorAll('.roulette-number-btn');
                              buttons.forEach(btn => {
                                const btnNum = btn.textContent.trim();
                                if ([row2Nums[colIdx], row3Nums[colIdx]].some(n => n == btnNum || n === btnNum)) {
                                  btn.style.transform = 'scale(1.05)';
                                  btn.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                                }
                              });
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            hideTooltip();
                            const buttons = document.querySelectorAll('.roulette-number-btn');
                            buttons.forEach(btn => {
                              btn.style.transform = 'scale(1)';
                              btn.style.boxShadow = btn.disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            });
                          }}
                          style={{
                            position: 'absolute',
                            top: 'calc(66.67% - 4px)',
                            left: `calc(${(colIdx / 12) * 100}% + 2px)`,
                            right: `calc(${((11 - colIdx) / 12) * 100}% + 2px)`,
                            height: '8px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            zIndex: 10,
                            pointerEvents: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {bets.filter(b => b.type === 'split' && JSON.stringify(b.value.sort()) === JSON.stringify([row2Nums[colIdx], row3Nums[colIdx]].sort())).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {/* Corner betting zones - where 4 numbers meet (invisible with tooltip) */}
                  {[...Array(11)].map((_, colIdx) => {
                    const row1Nums = [3,6,9,12,15,18,21,24,27,30,33,36];
                    const row2Nums = [2,5,8,11,14,17,20,23,26,29,32,35];
                    const row3Nums = [1,4,7,10,13,16,19,22,25,28,31,34];

                    return (
                      <React.Fragment key={`corner-${colIdx}`}>
                        {/* Corner between rows 1-2 */}
                        <div
                          id={`corner-r1r2-${colIdx}`}
                          onClick={() => !isBettingLocked && placeBet("corner", [row1Nums[colIdx], row1Nums[colIdx+1], row2Nums[colIdx], row2Nums[colIdx+1]])}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              showTooltip(e, `Corner: ${row1Nums[colIdx]}/${row1Nums[colIdx+1]}/${row2Nums[colIdx]}/${row2Nums[colIdx+1]}`, '8:1');
                              const buttons = document.querySelectorAll('.roulette-number-btn');
                              buttons.forEach(btn => {
                                const btnNum = btn.textContent.trim();
                                if ([row1Nums[colIdx], row1Nums[colIdx+1], row2Nums[colIdx], row2Nums[colIdx+1]].some(n => n == btnNum || n === btnNum)) {
                                  btn.style.transform = 'scale(1.05)';
                                  btn.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                                }
                              });
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            hideTooltip();
                            const buttons = document.querySelectorAll('.roulette-number-btn');
                            buttons.forEach(btn => {
                              btn.style.transform = 'scale(1)';
                              btn.style.boxShadow = btn.disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            });
                          }}
                          style={{
                            position: 'absolute',
                            top: 'calc(33.33% - 6px)',
                            left: `calc(${((colIdx + 1) / 12) * 100}% - 6px)`,
                            width: '12px',
                            height: '12px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            zIndex: 15,
                            pointerEvents: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {bets.filter(b => b.type === 'corner' && JSON.stringify(b.value.sort()) === JSON.stringify([row1Nums[colIdx], row1Nums[colIdx+1], row2Nums[colIdx], row2Nums[colIdx+1]].sort())).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </div>
                        {/* Corner between rows 2-3 */}
                        <div
                          id={`corner-r2r3-${colIdx}`}
                          onClick={() => !isBettingLocked && placeBet("corner", [row2Nums[colIdx], row2Nums[colIdx+1], row3Nums[colIdx], row3Nums[colIdx+1]])}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              showTooltip(e, `Corner: ${row2Nums[colIdx]}/${row2Nums[colIdx+1]}/${row3Nums[colIdx]}/${row3Nums[colIdx+1]}`, '8:1');
                              const buttons = document.querySelectorAll('.roulette-number-btn');
                              buttons.forEach(btn => {
                                const btnNum = btn.textContent.trim();
                                if ([row2Nums[colIdx], row2Nums[colIdx+1], row3Nums[colIdx], row3Nums[colIdx+1]].some(n => n == btnNum || n === btnNum)) {
                                  btn.style.transform = 'scale(1.05)';
                                  btn.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                                }
                              });
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            hideTooltip();
                            const buttons = document.querySelectorAll('.roulette-number-btn');
                            buttons.forEach(btn => {
                              btn.style.transform = 'scale(1)';
                              btn.style.boxShadow = btn.disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            });
                          }}
                          style={{
                            position: 'absolute',
                            top: 'calc(66.67% - 6px)',
                            left: `calc(${((colIdx + 1) / 12) * 100}% - 6px)`,
                            width: '12px',
                            height: '12px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            zIndex: 15,
                            pointerEvents: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {bets.filter(b => b.type === 'corner' && JSON.stringify(b.value.sort()) === JSON.stringify([row2Nums[colIdx], row2Nums[colIdx+1], row3Nums[colIdx], row3Nums[colIdx+1]].sort())).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {/* Street betting zones - on left edge for betting on 3 numbers in a row (invisible with tooltip) */}
                  {[0, 1, 2].map(rowIdx => {
                    const rowNums = [
                      [3,6,9,12,15,18,21,24,27,30,33,36],
                      [2,5,8,11,14,17,20,23,26,29,32,35],
                      [1,4,7,10,13,16,19,22,25,28,31,34]
                    ][rowIdx];

                    // Create street zones for every 3 consecutive numbers - placed on left edge of each group
                    return [...Array(4)].map((_, colSet) => {
                      const streetNums = rowNums.slice(colSet * 3, colSet * 3 + 3);
                      return (
                        <div
                          key={`street-${rowIdx}-${colSet}`}
                          id={`street-${rowIdx}-${colSet}`}
                          onClick={() => !isBettingLocked && placeBet("street", streetNums)}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              showTooltip(e, `Street: ${streetNums[0]}/${streetNums[1]}/${streetNums[2]}`, '11:1');
                              const buttons = document.querySelectorAll('.roulette-number-btn');
                              buttons.forEach(btn => {
                                const btnNum = btn.textContent.trim();
                                if (streetNums.some(n => n == btnNum || n === btnNum)) {
                                  btn.style.transform = 'scale(1.05)';
                                  btn.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                                }
                              });
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={e => {
                            hideTooltip();
                            const buttons = document.querySelectorAll('.roulette-number-btn');
                            buttons.forEach(btn => {
                              btn.style.transform = 'scale(1)';
                              btn.style.boxShadow = btn.disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            });
                          }}
                          style={{
                            position: 'absolute',
                            top: `calc(${(rowIdx / 3) * 100}% + 2px)`,
                            bottom: `calc(${((2 - rowIdx) / 3) * 100}% + 2px)`,
                            left: `calc(${((colSet * 3) / 12) * 100}%)`,
                            width: '8px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            zIndex: 20,
                            pointerEvents: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {bets.filter(b => b.type === 'street' && JSON.stringify(b.value.sort()) === JSON.stringify(streetNums.sort())).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </div>
                      );
                    });
                  }).flat()}

                  {/* Six-line betting zones - between two rows for 6 numbers (invisible with tooltip) */}
                  {[0, 1].map(pairIdx => {
                    const row1Nums = [
                      [3,6,9,12,15,18,21,24,27,30,33,36],
                      [2,5,8,11,14,17,20,23,26,29,32,35]
                    ][pairIdx];
                    const row2Nums = [
                      [2,5,8,11,14,17,20,23,26,29,32,35],
                      [1,4,7,10,13,16,19,22,25,28,31,34]
                    ][pairIdx];

                    // Create six-line zones for every 3 consecutive column pairs - on left edge between rows
                    return [...Array(4)].map((_, colSet) => {
                      const sixlineNums = [...row1Nums.slice(colSet * 3, colSet * 3 + 3), ...row2Nums.slice(colSet * 3, colSet * 3 + 3)];
                      return (
                        <div
                          key={`sixline-${pairIdx}-${colSet}`}
                          onClick={() => !isBettingLocked && placeBet("sixline", sixlineNums)}
                          onMouseEnter={e => {
                            if (!isBettingLocked) {
                              showTooltip(e, `Six-Line: ${sixlineNums[0]}-${sixlineNums[5]}`, '5:1');
                              const buttons = document.querySelectorAll('.roulette-number-btn');
                              buttons.forEach(btn => {
                                const btnNum = btn.textContent.trim();
                                if (sixlineNums.some(n => n == btnNum || n === btnNum)) {
                                  btn.style.transform = 'scale(1.05)';
                                  btn.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                                }
                              });
                            }
                          }}
                          onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                          onMouseLeave={() => {
                            hideTooltip();
                            const buttons = document.querySelectorAll('.roulette-number-btn');
                            buttons.forEach(btn => {
                              btn.style.transform = 'scale(1)';
                              btn.style.boxShadow = btn.disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
                            });
                          }}
                          style={{
                            position: 'absolute',
                            top: `calc(${((pairIdx + 1) / 3) * 100}% - 6px)`,
                            left: `calc(${((colSet * 3) / 12) * 100}%)`,
                            width: '8px',
                            height: '12px',
                            cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                            zIndex: 20,
                            pointerEvents: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {bets.filter(b => b.type === 'sixline' && JSON.stringify(b.value.sort()) === JSON.stringify(sixlineNums.sort())).map((bet, idx) => (
                            <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                          ))}
                        </div>
                      );
                    });
                  }).flat()}
                  </>}
                </div>

                {/* Column bets - right side "2 to 1" zones */}
                <div style={{ display: isMobile ? 'none' : 'flex', flexDirection: 'column', gap: '4px', minWidth: '60px' }}>
                  <button
                    onClick={() => placeBet("column", 1)}
                    disabled={isBettingLocked}
                    onMouseEnter={e => {
                      if (!isBettingLocked) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                        showTooltip(e, 'Column 1 (3-36)', '2:1');
                      }
                    }}
                    onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 8px rgba(138, 43, 226, 0.3)';
                      hideTooltip();
                    }}
                    style={{
                      flex: 1,
                      background: isBettingLocked ? 'rgba(138,43,226,0.2)' : 'linear-gradient(145deg, #8A2BE2 0%, #4B0082 100%)',
                      color: 'white',
                      border: '2px solid #9370DB',
                      borderRadius: '6px',
                      cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      boxShadow: isBettingLocked ? 'none' : '0 2px 8px rgba(138, 43, 226, 0.3)',
                      transition: 'all 0.2s',
                      padding: '8px 4px',
                      position: 'relative'
                    }}
                  >
                    2 to 1
                    {bets.filter(b => b.type === 'column' && b.value === 1).map((bet, idx) => (
                      <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                    ))}
                  </button>
                  <button
                    onClick={() => placeBet("column", 2)}
                    disabled={isBettingLocked}
                    onMouseEnter={e => {
                      if (!isBettingLocked) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                        showTooltip(e, 'Column 2 (2-35)', '2:1');
                      }
                    }}
                    onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 8px rgba(138, 43, 226, 0.3)';
                      hideTooltip();
                    }}
                    style={{
                      flex: 1,
                      background: isBettingLocked ? 'rgba(138,43,226,0.2)' : 'linear-gradient(145deg, #8A2BE2 0%, #4B0082 100%)',
                      color: 'white',
                      border: '2px solid #9370DB',
                      borderRadius: '6px',
                      cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      boxShadow: isBettingLocked ? 'none' : '0 2px 8px rgba(138, 43, 226, 0.3)',
                      transition: 'all 0.2s',
                      padding: '8px 4px',
                      position: 'relative'
                    }}
                  >
                    2 to 1
                    {bets.filter(b => b.type === 'column' && b.value === 2).map((bet, idx) => (
                      <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                    ))}
                  </button>
                  <button
                    onClick={() => placeBet("column", 3)}
                    disabled={isBettingLocked}
                    onMouseEnter={e => {
                      if (!isBettingLocked) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                        showTooltip(e, 'Column 3 (1-34)', '2:1');
                      }
                    }}
                    onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 8px rgba(138, 43, 226, 0.3)';
                      hideTooltip();
                    }}
                    style={{
                      flex: 1,
                      background: isBettingLocked ? 'rgba(138,43,226,0.2)' : 'linear-gradient(145deg, #8A2BE2 0%, #4B0082 100%)',
                      color: 'white',
                      border: '2px solid #9370DB',
                      borderRadius: '6px',
                      cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      boxShadow: isBettingLocked ? 'none' : '0 2px 8px rgba(138, 43, 226, 0.3)',
                      transition: 'all 0.2s',
                      padding: '8px 4px',
                      position: 'relative'
                    }}
                  >
                    2 to 1
                    {bets.filter(b => b.type === 'column' && b.value === 3).map((bet, idx) => (
                      <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                    ))}
                  </button>
                </div>
              </div>

              {/* Dozen bets below main grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: isMobile ? '4px' : '8px', marginLeft: isMobile ? '0' : '63px' }}>
                <button
                  onClick={() => placeBet("dozen", 1)}
                  disabled={isBettingLocked}
                  onMouseEnter={e => {
                    if (!isBettingLocked) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                      showTooltip(e, '1st Dozen (1-12)', '2:1');
                    }
                  }}
                  onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 10px rgba(138, 43, 226, 0.3)';
                    hideTooltip();
                  }}
                  style={{
                    padding: '14px',
                    background: isBettingLocked ? 'rgba(138,43,226,0.2)' : 'linear-gradient(145deg, #8A2BE2 0%, #4B0082 100%)',
                    color: 'white',
                    border: '2px solid #9370DB',
                    borderRadius: '8px',
                    cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: isBettingLocked ? 'none' : '0 2px 10px rgba(138, 43, 226, 0.3)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                >
                  1st 12<br/><span style={{ fontSize: '10px', opacity: 0.9 }}>2:1</span>
                  {bets.filter(b => b.type === 'dozen' && b.value === 1).map((bet, idx) => (
                    <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                  ))}
                </button>
                <button
                  onClick={() => placeBet("dozen", 2)}
                  disabled={isBettingLocked}
                  onMouseEnter={e => {
                    if (!isBettingLocked) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                      showTooltip(e, '2nd Dozen (13-24)', '2:1');
                    }
                  }}
                  onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 10px rgba(138, 43, 226, 0.3)';
                    hideTooltip();
                  }}
                  style={{
                    padding: '14px',
                    background: isBettingLocked ? 'rgba(138,43,226,0.2)' : 'linear-gradient(145deg, #8A2BE2 0%, #4B0082 100%)',
                    color: 'white',
                    border: '2px solid #9370DB',
                    borderRadius: '8px',
                    cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: isBettingLocked ? 'none' : '0 2px 10px rgba(138, 43, 226, 0.3)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                >
                  2nd 12<br/><span style={{ fontSize: '10px', opacity: 0.9 }}>2:1</span>
                  {bets.filter(b => b.type === 'dozen' && b.value === 2).map((bet, idx) => (
                    <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                  ))}
                </button>
                <button
                  onClick={() => placeBet("dozen", 3)}
                  disabled={isBettingLocked}
                  onMouseEnter={e => {
                    if (!isBettingLocked) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                      showTooltip(e, '3rd Dozen (25-36)', '2:1');
                    }
                  }}
                  onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 10px rgba(138, 43, 226, 0.3)';
                    hideTooltip();
                  }}
                  style={{
                    padding: '14px',
                    background: isBettingLocked ? 'rgba(138,43,226,0.2)' : 'linear-gradient(145deg, #8A2BE2 0%, #4B0082 100%)',
                    color: 'white',
                    border: '2px solid #9370DB',
                    borderRadius: '8px',
                    cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: isBettingLocked ? 'none' : '0 2px 10px rgba(138, 43, 226, 0.3)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                >
                  3rd 12<br/><span style={{ fontSize: '10px', opacity: 0.9 }}>2:1</span>
                  {bets.filter(b => b.type === 'dozen' && b.value === 3).map((bet, idx) => (
                    <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                  ))}
                </button>
              </div>

              {/* Outside bets - red/black, even/odd, ranges */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)', gap: isMobile ? '4px' : '8px', marginLeft: isMobile ? '0' : '63px' }}>
                <button
                  onClick={() => placeBet("range", "1-18")}
                  disabled={isBettingLocked}
                  style={{
                    padding: '14px 8px',
                    background: isBettingLocked ? 'rgba(34,139,34,0.2)' : 'linear-gradient(145deg, #228B22 0%, #006400 100%)',
                    color: 'white',
                    border: '2px solid #32CD32',
                    borderRadius: '8px',
                    cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    boxShadow: isBettingLocked ? 'none' : '0 2px 10px rgba(34, 139, 34, 0.3)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={e => {
                    if (!isBettingLocked) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                      showTooltip(e, 'Low Numbers (1-18)', '1:1');
                    }
                  }}
                  onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 10px rgba(34, 139, 34, 0.3)';
                    hideTooltip();
                  }}
                >
                  1-18<br/><span style={{ fontSize: '9px', opacity: 0.9 }}>1:1</span>
                  {bets.filter(b => b.type === 'range' && b.value === '1-18').map((bet, idx) => (
                    <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                  ))}
                </button>
                <button
                  onClick={() => placeBet("even", null)}
                  disabled={isBettingLocked}
                  style={{
                    padding: '14px 8px',
                    background: isBettingLocked ? 'rgba(34,139,34,0.2)' : 'linear-gradient(145deg, #228B22 0%, #006400 100%)',
                    color: 'white',
                    border: '2px solid #32CD32',
                    borderRadius: '8px',
                    cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    boxShadow: isBettingLocked ? 'none' : '0 2px 10px rgba(34, 139, 34, 0.3)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={e => {
                    if (!isBettingLocked) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                      showTooltip(e, 'Even Numbers', '1:1');
                    }
                  }}
                  onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 10px rgba(34, 139, 34, 0.3)';
                    hideTooltip();
                  }}
                >
                  EVEN<br/><span style={{ fontSize: '9px', opacity: 0.9 }}>1:1</span>
                  {bets.filter(b => b.type === 'even').map((bet, idx) => (
                    <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                  ))}
                </button>
                <button
                  onClick={() => placeBet("red", null)}
                  disabled={isBettingLocked}
                  style={{
                    padding: '14px 8px',
                    background: isBettingLocked ? 'rgba(220,20,60,0.3)' : 'linear-gradient(145deg, #DC143C 0%, #8B0000 100%)',
                    color: 'white',
                    border: '2px solid #FF6B6B',
                    borderRadius: '8px',
                    cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    boxShadow: isBettingLocked ? 'none' : '0 2px 10px rgba(220, 20, 60, 0.3)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={e => {
                    if (!isBettingLocked) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                      showTooltip(e, 'Red Numbers', '1:1');
                    }
                  }}
                  onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 10px rgba(220, 20, 60, 0.3)';
                    hideTooltip();
                  }}
                >
                  RED<br/><span style={{ fontSize: '9px', opacity: 0.9 }}>1:1</span>
                  {bets.filter(b => b.type === 'red').map((bet, idx) => (
                    <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                  ))}
                </button>
                <button
                  onClick={() => placeBet("black", null)}
                  disabled={isBettingLocked}
                  style={{
                    padding: '14px 8px',
                    background: isBettingLocked ? 'rgba(0,0,0,0.3)' : 'linear-gradient(145deg, #1a1a1a 0%, #000 100%)',
                    color: 'white',
                    border: '2px solid #444',
                    borderRadius: '8px',
                    cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    boxShadow: isBettingLocked ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.5)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={e => {
                    if (!isBettingLocked) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                      showTooltip(e, 'Black Numbers', '1:1');
                    }
                  }}
                  onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.5)';
                    hideTooltip();
                  }}
                >
                  BLACK<br/><span style={{ fontSize: '9px', opacity: 0.9 }}>1:1</span>
                  {bets.filter(b => b.type === 'black').map((bet, idx) => (
                    <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                  ))}
                </button>
                <button
                  onClick={() => placeBet("odd", null)}
                  disabled={isBettingLocked}
                  style={{
                    padding: '14px 8px',
                    background: isBettingLocked ? 'rgba(34,139,34,0.2)' : 'linear-gradient(145deg, #228B22 0%, #006400 100%)',
                    color: 'white',
                    border: '2px solid #32CD32',
                    borderRadius: '8px',
                    cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    boxShadow: isBettingLocked ? 'none' : '0 2px 10px rgba(34, 139, 34, 0.3)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={e => {
                    if (!isBettingLocked) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                      showTooltip(e, 'Odd Numbers', '1:1');
                    }
                  }}
                  onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 10px rgba(34, 139, 34, 0.3)';
                    hideTooltip();
                  }}
                >
                  ODD<br/><span style={{ fontSize: '9px', opacity: 0.9 }}>1:1</span>
                  {bets.filter(b => b.type === 'odd').map((bet, idx) => (
                    <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                  ))}
                </button>
                <button
                  onClick={() => placeBet("range", "19-36")}
                  disabled={isBettingLocked}
                  style={{
                    padding: '14px 8px',
                    background: isBettingLocked ? 'rgba(34,139,34,0.2)' : 'linear-gradient(145deg, #228B22 0%, #006400 100%)',
                    color: 'white',
                    border: '2px solid #32CD32',
                    borderRadius: '8px',
                    cursor: isBettingLocked ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    boxShadow: isBettingLocked ? 'none' : '0 2px 10px rgba(34, 139, 34, 0.3)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={e => {
                    if (!isBettingLocked) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 15px 3px rgba(255, 255, 255, 0.8)';
                      showTooltip(e, 'High Numbers (19-36)', '1:1');
                    }
                  }}
                  onMouseMove={e => !isBettingLocked && moveTooltip(e)}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isBettingLocked ? 'none' : '0 2px 10px rgba(34, 139, 34, 0.3)';
                    hideTooltip();
                  }}
                >
                  19-36<br/><span style={{ fontSize: '9px', opacity: 0.9 }}>1:1</span>
                  {bets.filter(b => b.type === 'range' && b.value === '19-36').map((bet, idx) => (
                    <React.Fragment key={idx}>{renderTokenChip(bet)}</React.Fragment>
                  ))}
                </button>
              </div>
            </div>
          </div>
        </div>

        </div>{/* end right content */}

        {/* ── MOBILE BOTTOM CONTROLS BAR ── */}
        {isMobile && (
          <div style={{
            position: 'fixed', bottom: '56px', left: 0, right: 0,
            background: 'linear-gradient(180deg, rgba(15,15,15,0.97) 0%, rgba(10,10,10,0.99) 100%)',
            borderTop: '2px solid rgba(255,215,0,0.25)',
            padding: '8px 10px',
            zIndex: 90,
            backdropFilter: 'blur(12px)',
            display: 'flex', flexDirection: 'column', gap: '6px',
          }}>
            {/* Row 1: Token type + Bet amount + Quick bets */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {/* Back button */}
              <button onClick={onBack} style={{
                padding: '8px', background: 'rgba(60,60,60,0.8)', border: '1px solid rgba(100,100,100,0.4)',
                borderRadius: '6px', color: '#aaa', cursor: 'pointer', fontSize: '14px', flexShrink: 0,
              }}>←</button>

              {/* Token toggle */}
              <button onClick={() => setTokenType(tokenType === "crewmate" ? "imposter" : "crewmate")} style={{
                padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', flexShrink: 0,
                background: tokenType === "crewmate" ? 'rgba(65,105,225,0.3)' : 'rgba(220,20,60,0.3)',
                border: `1.5px solid ${tokenType === "crewmate" ? '#6495ED' : '#FF6B6B'}`,
                color: '#fff',
              }}>{tokenType === "crewmate" ? "🔵" : "🔴"}</button>

              {/* Bet amount input */}
              <input
                type="number" min="0.01" step="0.01" value={betAmount}
                onChange={(e) => setBetAmount(Math.max(0.01, parseFloat(e.target.value) || 0.01))}
                style={{
                  width: '70px', padding: '8px 6px', background: 'rgba(30,30,30,0.9)',
                  border: '1.5px solid rgba(255,215,0,0.3)', borderRadius: '6px',
                  color: '#FFD700', fontSize: '16px', fontWeight: 'bold', textAlign: 'center',
                  outline: 'none', flexShrink: 0,
                }}
              />

              {/* Quick bet buttons */}
              <div style={{ display: 'flex', gap: '3px', flex: 1, overflow: 'hidden' }}>
                {[0.5, 1, 5, 10].map(a => (
                  <button key={a} onClick={() => setBetAmount(a)} style={{
                    flex: 1, padding: '7px 2px', borderRadius: '5px', cursor: 'pointer',
                    fontSize: '11px', fontWeight: 'bold', minWidth: 0,
                    background: betAmount === a ? 'rgba(255,215,0,0.2)' : 'rgba(50,50,50,0.6)',
                    border: betAmount === a ? '1.5px solid #FFD700' : '1px solid rgba(100,100,100,0.3)',
                    color: betAmount === a ? '#FFD700' : '#888',
                  }}>{a}</button>
                ))}
              </div>
            </div>

            {/* Row 2: Your bets summary + Clear */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', flex: 1 }}>
                {bets.length === 0 ? 'Tap numbers to place bets' : (
                  <span>
                    <span style={{ color: '#FFD700', fontWeight: '700' }}>{bets.length}</span> bet{bets.length !== 1 ? 's' : ''} — <span style={{ color: '#FFD700', fontWeight: '700' }}>{totalBetAmount}</span> {tokenType === 'crewmate' ? '🔵' : '🔴'}
                  </span>
                )}
              </div>
              {bets.length > 0 && (
                <button onClick={clearBets} disabled={isBettingLocked} style={{
                  padding: '6px 12px', background: 'rgba(220,20,60,0.2)',
                  border: '1px solid rgba(220,20,60,0.4)', borderRadius: '6px',
                  color: '#FF6B6B', fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                }}>🗑️ Clear</button>
              )}
            </div>
          </div>
        )}
    </div>
  );
}

export default RoulettePage;
